// Tile constants (must match levels.ts / engine.ts)
const Tile = {
  Floor: 0,
  Wall: 1,
  Target: 2,
  Box: 3,
  BoxOnTarget: 4,
  Player: 5,
  PlayerOnTarget: 6,
} as const;

// ---- Seeded PRNG (mulberry32) ----

export function hashString(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  // MurmurHash3 finalization — spreads sequential values far apart
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

export function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rng: () => number, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}

function shuffle<T>(rng: () => number, arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ---- Grid helpers ----

const DIRS = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

function inBounds(r: number, c: number, h: number, w: number): boolean {
  return r >= 0 && r < h && c >= 0 && c < w;
}

function floodFill(grid: number[][], startR: number, startC: number): Set<string> {
  const h = grid.length;
  const w = grid[0].length;
  const visited = new Set<string>();
  const stack = [[startR, startC]];
  while (stack.length > 0) {
    const [r, c] = stack.pop()!;
    const key = `${r},${c}`;
    if (visited.has(key)) continue;
    if (!inBounds(r, c, h, w) || grid[r][c] === Tile.Wall) continue;
    visited.add(key);
    for (const [dr, dc] of DIRS) {
      stack.push([r + dr, c + dc]);
    }
  }
  return visited;
}

function isCorner(grid: number[][], r: number, c: number): boolean {
  const h = grid.length;
  const w = grid[0].length;
  const wallAt = (rr: number, cc: number) =>
    !inBounds(rr, cc, h, w) || grid[rr][cc] === Tile.Wall;

  return (
    (wallAt(r - 1, c) && wallAt(r, c - 1)) ||
    (wallAt(r - 1, c) && wallAt(r, c + 1)) ||
    (wallAt(r + 1, c) && wallAt(r, c - 1)) ||
    (wallAt(r + 1, c) && wallAt(r, c + 1))
  );
}

// ---- BFS Solver ----

const MAX_BFS_STATES = 30_000;

/**
 * BFS solver for Sokoban puzzles. Returns { moves, statesExplored } or null
 * if the state-budget is exhausted (puzzle is hard / unsolvable within budget).
 *
 * Counts **pushes** (box moves), not total player steps.
 *
 * State key: (canonical player position, sorted box positions).
 * Player position is normalized to the minimum reachable cell to collapse
 * equivalent positions where the player is in the same connected area.
 */
export function solveBFS(
  grid: number[][],
  maxStates: number = MAX_BFS_STATES,
): { moves: number; statesExplored: number } | null {
  const h = grid.length;
  const w = grid[0].length;
  const sz = h * w;

  // Extract initial state from grid
  let playerPos = -1;
  const boxes: number[] = [];
  const targetSet = new Set<number>();

  for (let r = 0; r < h; r++) {
    for (let c = 0; c < w; c++) {
      const t = grid[r][c];
      const idx = r * w + c;
      if (t === Tile.Player || t === Tile.PlayerOnTarget) playerPos = idx;
      if (t === Tile.Box || t === Tile.BoxOnTarget) boxes.push(idx);
      if (t === Tile.Target || t === Tile.BoxOnTarget || t === Tile.PlayerOnTarget) targetSet.add(idx);
    }
  }

  if (playerPos === -1 || boxes.length === 0) return null;

  // Build static wall set (flat index)
  const isWall = new Uint8Array(sz);
  for (let r = 0; r < h; r++) {
    for (let c = 0; c < w; c++) {
      if (grid[r][c] === Tile.Wall) isWall[r * w + c] = 1;
    }
  }

  const dirDR = [-1, 1, 0, 0];
  const dirDC = [0, 0, -1, 1];

  // Target bitmap for deadlock checks
  const targetBitmap = new Uint8Array(sz);
  for (const t of targetSet) targetBitmap[t] = 1;

  // Corner deadlock check (static — doesn't change with box placement)
  const isStaticCorner = new Uint8Array(sz);
  for (let r = 0; r < h; r++) {
    for (let c = 0; c < w; c++) {
      const idx = r * w + c;
      if (isWall[idx] || targetBitmap[idx]) continue;
      const wallUp = r === 0 || isWall[(r - 1) * w + c];
      const wallDown = r === h - 1 || isWall[(r + 1) * w + c];
      const wallLeft = c === 0 || isWall[r * w + (c - 1)];
      const wallRight = c === w - 1 || isWall[r * w + (c + 1)];
      if ((wallUp && wallLeft) || (wallUp && wallRight) ||
          (wallDown && wallLeft) || (wallDown && wallRight)) {
        isStaticCorner[idx] = 1;
      }
    }
  }

  // Reusable scratch buffer for flood-fill
  const reachBuf = new Uint8Array(sz);
  const fillStack: number[] = new Array(sz);

  /**
   * Flood-fill reachable cells. Writes into reachBuf (caller must clear after use).
   * Returns minimum reachable flat index (canonical player position).
   */
  function floodReachable(pIdx: number, boxSet: Set<number>): number {
    let minIdx = pIdx;
    reachBuf[pIdx] = 1;
    fillStack[0] = pIdx;
    let sp = 0;
    let fp = 1;
    while (sp < fp) {
      const cur = fillStack[sp++];
      const cr = Math.floor(cur / w);
      const cc = cur % w;
      for (let d = 0; d < 4; d++) {
        const nr = cr + dirDR[d];
        const nc = cc + dirDC[d];
        if (nr < 0 || nr >= h || nc < 0 || nc >= w) continue;
        const ni = nr * w + nc;
        if (reachBuf[ni] || isWall[ni] || boxSet.has(ni)) continue;
        reachBuf[ni] = 1;
        if (ni < minIdx) minIdx = ni;
        fillStack[fp++] = ni;
      }
    }
    return minIdx;
  }

  // Check if placing a box at idx creates a simple deadlock
  function isDeadBox(idx: number): boolean {
    if (targetBitmap[idx]) return false;
    if (isStaticCorner[idx]) return true;
    // Wall-line deadlock (horizontal)
    const r = Math.floor(idx / w);
    const c = idx % w;
    for (const dr of [-1, 1]) {
      const adjR = r + dr;
      if (adjR < 0 || adjR >= h || !isWall[adjR * w + c]) continue;
      let foundEscape = false;
      for (let cc = c - 1; cc >= 0; cc--) {
        if (isWall[r * w + cc]) break;
        if (!isWall[adjR * w + cc]) { foundEscape = true; break; }
        if (targetBitmap[r * w + cc]) { foundEscape = true; break; }
      }
      if (foundEscape) continue;
      for (let cc = c + 1; cc < w; cc++) {
        if (isWall[r * w + cc]) break;
        if (!isWall[adjR * w + cc]) { foundEscape = true; break; }
        if (targetBitmap[r * w + cc]) { foundEscape = true; break; }
      }
      if (!foundEscape) return true;
    }
    // Wall-line deadlock (vertical)
    for (const dc of [-1, 1]) {
      const adjC = c + dc;
      if (adjC < 0 || adjC >= w || !isWall[r * w + adjC]) continue;
      let foundEscape = false;
      for (let rr = r - 1; rr >= 0; rr--) {
        if (isWall[rr * w + c]) break;
        if (!isWall[rr * w + adjC]) { foundEscape = true; break; }
        if (targetBitmap[rr * w + c]) { foundEscape = true; break; }
      }
      if (foundEscape) continue;
      for (let rr = r + 1; rr < h; rr++) {
        if (isWall[rr * w + c]) break;
        if (!isWall[rr * w + adjC]) { foundEscape = true; break; }
        if (targetBitmap[rr * w + c]) { foundEscape = true; break; }
      }
      if (!foundEscape) return true;
    }
    return false;
  }

  // Initial state
  const initBoxes = boxes.slice().sort((a, b) => a - b);
  const initBoxSet = new Set(initBoxes);
  const initCanon = floodReachable(playerPos, initBoxSet);
  // Count how many cells were filled (sp in fillStack)
  // We need to find the fill count — it's the fp value from the last call
  // But we can't access fp from here. Let me restructure.
  // Actually, let me just clear the whole reachBuf for the initial call.
  reachBuf.fill(0);

  const initKey = `${initCanon}|${initBoxes.join(',')}`;

  // Check if already solved
  if (initBoxes.every(b => targetSet.has(b))) return { moves: 0, statesExplored: 1 };

  // BFS queue: [canonPlayerIdx, boxPositions[], moves]
  const queue: [number, number[], number][] = [[initCanon, initBoxes, 0]];
  let head = 0;
  const visited = new Set<string>();
  visited.add(initKey);

  while (head < queue.length) {
    if (visited.size >= maxStates) return null;

    const [canonPos, curBoxes, curMoves] = queue[head++];
    const curBoxSet = new Set(curBoxes);

    // Compute reachable set ONCE for this state
    const canon = floodReachable(canonPos, curBoxSet);
    // Save filled cells count by scanning fillStack
    // Actually, we know fillStack[0..fp-1] are the filled cells.
    // But fp is local to floodReachable. Let me refactor.
    // For now, just snapshot the reachable bitmap.
    const reachable = new Uint8Array(reachBuf);
    reachBuf.fill(0); // clear for next use

    // For each box, try pushing in each direction
    for (let bi = 0; bi < curBoxes.length; bi++) {
      const boxIdx = curBoxes[bi];
      const boxR = Math.floor(boxIdx / w);
      const boxC = boxIdx % w;

      for (let d = 0; d < 4; d++) {
        const dr = dirDR[d];
        const dc = dirDC[d];

        // Player must be on the opposite side of push direction
        const pushFromR = boxR - dr;
        const pushFromC = boxC - dc;
        if (pushFromR < 0 || pushFromR >= h || pushFromC < 0 || pushFromC >= w) continue;
        const pushFromIdx = pushFromR * w + pushFromC;
        if (isWall[pushFromIdx] || curBoxSet.has(pushFromIdx)) continue;

        // Player must be able to reach push-from cell
        if (!reachable[pushFromIdx]) continue;

        // Box destination
        const pushToR = boxR + dr;
        const pushToC = boxC + dc;
        if (pushToR < 0 || pushToR >= h || pushToC < 0 || pushToC >= w) continue;
        const pushToIdx = pushToR * w + pushToC;
        if (isWall[pushToIdx] || curBoxSet.has(pushToIdx)) continue;

        // Deadlock check on destination
        if (isDeadBox(pushToIdx)) continue;

        // New box configuration
        const newBoxes = curBoxes.slice();
        newBoxes[bi] = pushToIdx;
        newBoxes.sort((a, b) => a - b);

        // After push, player is at boxIdx (the old box position)
        const newBoxSet = new Set(newBoxes);
        const newCanon = floodReachable(boxIdx, newBoxSet);
        reachBuf.fill(0);
        const newKey = `${newCanon}|${newBoxes.join(',')}`;

        if (visited.has(newKey)) continue;
        visited.add(newKey);

        // Check solved
        if (newBoxes.every(b => targetSet.has(b))) {
          return { moves: curMoves + 1, statesExplored: visited.size };
        }

        queue.push([newCanon, newBoxes, curMoves + 1]);
      }
    }
  }

  // Exhausted all reachable states without solving — puzzle is unsolvable
  return null;
}

// ---- Room generation ----

function generateRoom(
  rng: () => number,
  gridH: number,
  gridW: number,
  targetFloorCells: number,
): number[][] {
  // Fill with walls
  const grid: number[][] = [];
  for (let r = 0; r < gridH; r++) {
    grid.push(new Array(gridW).fill(Tile.Wall));
  }

  // Random walk from center to carve floor
  let r = Math.floor(gridH / 2);
  let c = Math.floor(gridW / 2);
  grid[r][c] = Tile.Floor;
  let carved = 1;

  while (carved < targetFloorCells) {
    const [dr, dc] = DIRS[randInt(rng, 0, 3)];
    const nr = r + dr;
    const nc = c + dc;
    // Stay away from edges (leave 1-cell wall border)
    if (nr >= 1 && nr < gridH - 1 && nc >= 1 && nc < gridW - 1) {
      if (grid[nr][nc] === Tile.Wall) {
        grid[nr][nc] = Tile.Floor;
        carved++;
      }
      r = nr;
      c = nc;
    }
  }

  return grid;
}

export function keepLargestComponent(grid: number[][]): number[][] {
  const h = grid.length;
  const w = grid[0].length;

  // Find all floor cells
  const floorCells: [number, number][] = [];
  for (let r = 0; r < h; r++) {
    for (let c = 0; c < w; c++) {
      if (grid[r][c] === Tile.Floor) floorCells.push([r, c]);
    }
  }
  if (floorCells.length === 0) return grid;

  // Find largest connected component
  const visited = new Set<string>();
  let bestComponent = new Set<string>();

  for (const [sr, sc] of floorCells) {
    const key = `${sr},${sc}`;
    if (visited.has(key)) continue;
    const component = floodFill(grid, sr, sc);
    for (const k of component) visited.add(k);
    if (component.size > bestComponent.size) bestComponent = component;
  }

  // Remove floor cells not in the largest component
  for (let r = 0; r < h; r++) {
    for (let c = 0; c < w; c++) {
      if (grid[r][c] === Tile.Floor && !bestComponent.has(`${r},${c}`)) {
        grid[r][c] = Tile.Wall;
      }
    }
  }

  return grid;
}

// ---- Reverse-play puzzle generation ----

export function generatePuzzleFromSeed(seed: number): GeneratedPuzzle | null {
  const rng = mulberry32(seed);

  // Choose box count (3-5) and grid size based on it
  const boxCount = randInt(rng, 3, 5);
  const gridSize = boxCount <= 3 ? 9 : boxCount === 4 ? 10 : 12;
  const targetFloor = randInt(rng, 20, 35);

  // Generate room
  const grid = generateRoom(rng, gridSize, gridSize, Math.min(targetFloor, (gridSize - 2) * (gridSize - 2)));
  keepLargestComponent(grid);

  // Collect interior (non-corner) floor cells for target placement
  const floorCells: [number, number][] = [];
  for (let r = 1; r < gridSize - 1; r++) {
    for (let c = 1; c < gridSize - 1; c++) {
      if (grid[r][c] === Tile.Floor && !isCorner(grid, r, c)) {
        floorCells.push([r, c]);
      }
    }
  }

  if (floorCells.length < boxCount + 1) return null; // not enough space

  // Pick target positions
  const shuffled = shuffle(rng, floorCells);
  const targets = shuffled.slice(0, boxCount);
  const targetSet = new Set(targets.map(([r, c]) => `${r},${c}`));

  // Place boxes on targets (solved state)
  for (const [r, c] of targets) {
    grid[r][c] = Tile.BoxOnTarget;
  }

  // Place player on a random floor cell that's not a target
  const playerCandidates = shuffled.slice(boxCount);
  if (playerCandidates.length === 0) return null;
  const [pr, pc] = playerCandidates[0];
  grid[pr][pc] = Tile.Player;

  // Verify player can reach at least one box area (connectivity)
  const reachable = floodFill(grid, pr, pc);
  // Player should be able to move — at least 3 reachable cells
  if (reachable.size < 3) return null;

  // ---- Reverse play ----
  // In reverse: player moves around, and when moving AWAY from a box
  // the box follows (pull). This reverses a push.
  let playerR = pr;
  let playerC = pc;
  const steps = randInt(rng, 200, 400);
  let pullCount = 0;

  for (let i = 0; i < steps; i++) {
    const dirIdx = randInt(rng, 0, 3);
    const [dr, dc] = DIRS[dirIdx];
    const nr = playerR + dr;
    const nc = playerC + dc;

    if (!inBounds(nr, nc, gridSize, gridSize)) continue;
    const targetTile = grid[nr][nc];
    if (targetTile === Tile.Wall || targetTile === Tile.Box || targetTile === Tile.BoxOnTarget) continue;

    // Check if there's a box behind the player (opposite direction) that we can pull
    const behindR = playerR - dr;
    const behindC = playerC - dc;

    if (
      inBounds(behindR, behindC, gridSize, gridSize) &&
      (grid[behindR][behindC] === Tile.Box || grid[behindR][behindC] === Tile.BoxOnTarget)
    ) {
      // Pull the box: box moves from behind to player's current position
      const boxWasOnTarget = grid[behindR][behindC] === Tile.BoxOnTarget;
      grid[behindR][behindC] = boxWasOnTarget ? Tile.Target : Tile.Floor;

      // Player's current cell: place box (check if it's a target)
      const playerWasOnTarget = grid[playerR][playerC] === Tile.PlayerOnTarget;
      grid[playerR][playerC] = playerWasOnTarget ? Tile.BoxOnTarget : Tile.Box;

      pullCount++;
    } else {
      // Just move player
      const playerWasOnTarget = grid[playerR][playerC] === Tile.PlayerOnTarget;
      grid[playerR][playerC] = playerWasOnTarget ? Tile.Target : Tile.Floor;
    }

    // Move player to new cell
    const isTarget = targetTile === Tile.Target || targetTile === Tile.PlayerOnTarget;
    grid[nr][nc] = isTarget ? Tile.PlayerOnTarget : Tile.Player;
    playerR = nr;
    playerC = nc;
  }

  // ---- Quality checks ----

  // 1. Check not already solved (at least one box not on target)
  const boxPositions: [number, number][] = [];
  const targetPositions: [number, number][] = [];
  for (let r = 0; r < gridSize; r++) {
    for (let c = 0; c < gridSize; c++) {
      if (grid[r][c] === Tile.Box) boxPositions.push([r, c]);
      if (grid[r][c] === Tile.BoxOnTarget) {
        boxPositions.push([r, c]);
        targetPositions.push([r, c]);
      }
      if (grid[r][c] === Tile.Target || grid[r][c] === Tile.PlayerOnTarget) {
        targetPositions.push([r, c]);
      }
    }
  }

  const boxesOnTarget = boxPositions.filter(([br, bc]) =>
    grid[br][bc] === Tile.BoxOnTarget
  ).length;
  if (boxesOnTarget === boxCount) return null; // puzzle is solved

  // 2. No box stuck in a wall corner (dead end)
  for (const [br, bc] of boxPositions) {
    if (grid[br][bc] === Tile.BoxOnTarget) continue;
    if (isCorner(grid, br, bc)) return null;
  }

  // 3. Must have had at least 2 pulls (meaningful puzzle)
  if (pullCount < 2) return null;

  // 4. BFS verification — reject trivially easy puzzles
  const bfsResult = solveBFS(grid, MAX_BFS_STATES);
  if (bfsResult !== null) {
    // BFS found the optimal solution (in pushes)
    if (bfsResult.moves < 8) return null;              // too easy
    if (bfsResult.statesExplored < 50) return null;    // trivial search space
    return {
      grid,
      width: gridSize,
      height: gridSize,
      optimalMoves: bfsResult.moves,
      title: 'Daily Puzzle',
    };
  }

  // BFS exhausted budget — puzzle is genuinely hard; accept with pullCount estimate
  return {
    grid,
    width: gridSize,
    height: gridSize,
    optimalMoves: pullCount,
    title: 'Daily Puzzle',
  };
}

// ---- Public API ----

export interface GeneratedPuzzle {
  grid: number[][];
  width: number;
  height: number;
  optimalMoves: number;
  title: string;
}

export function generateDailyPuzzle(
  dateStr: string,
  _seedGenerator?: (seed: number) => GeneratedPuzzle | null,
): GeneratedPuzzle {
  const baseSeed = hashString(dateStr);
  const tryGenerate = _seedGenerator ?? generatePuzzleFromSeed;

  // Try up to 200 seed variations
  for (let attempt = 0; attempt < 200; attempt++) {
    const seed = (baseSeed + attempt) >>> 0;
    const puzzle = tryGenerate(seed);
    if (puzzle) return puzzle;
  }

  // Fallback: hand-crafted 3-box puzzle (13 optimal pushes, BFS-verified)
  // This should rarely (if ever) happen with 200 attempts
  return {
    grid: [
      [1, 1, 1, 1, 1, 1, 1, 1, 1],
      [1, 0, 0, 0, 0, 0, 0, 0, 1],
      [1, 0, 0, 1, 3, 1, 0, 0, 1],
      [1, 0, 0, 0, 0, 0, 3, 0, 1],
      [1, 0, 1, 2, 0, 2, 1, 0, 1],
      [1, 0, 0, 0, 5, 0, 0, 0, 1],
      [1, 0, 3, 0, 0, 0, 2, 0, 1],
      [1, 0, 0, 0, 0, 0, 0, 0, 1],
      [1, 1, 1, 1, 1, 1, 1, 1, 1],
    ],
    width: 9,
    height: 9,
    optimalMoves: 13,
    title: 'Daily Puzzle',
  };
}
